import { FC } from "react";
import { useBreakpoints } from "@otrium/core";
import { Carousel } from "src/molecules/Carousel";
import { CarouselHeader } from "src/molecules/CarouselHeader";
import { CarouselOverlay } from "src/molecules/CarouselOverlay";
import { ProductCardWishlist } from "src/organisms/ProductCardWishlist";
import { useSegmentProductList } from "src/hooks/useSegmentProductList";
import { SEGMENT_LIST_FORMAT, SEGMENT_LIST_TYPE } from "src/segment";
import { useImageHeight } from "src/hooks/useImageHeight";
import { DeprecatedAlgoliaSearchProduct } from "src/types/Algolia.d";
import { CatalogBrand, CatalogProduct } from "src/types/graphql.d";
import { ProductFilteredLabels } from "src/hooks/useFilterProductLabels";
import { PLP_PAGE_TYPE } from "src/hooks/useSegment";
import { PLPType } from "src/types/PLPType.d";
import { GATED_POPUP_REDIRECT_DATA } from "src/constants/gatedItem";
import { ProductRedirect } from "src/organisms/ProductCardWishlist/ProductCardWishlist";
import {
  removeStorageItem,
  setStorageJSON,
} from "src/utils/helper/localStorage";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";

interface IconicCarouselProps {
  genderId: string;
  localeGenderId: string;
  headerLinkAs?: string;
  headerLinkHref?: string;
  maxItems?: number;
  products?: DeprecatedAlgoliaSearchProduct[];
  slidesDesktop?: number;
  slidesMobile?: number;
  slidesTablet?: number;
  subTitle?: string;
  title?: string;
  labels?: ProductFilteredLabels;
}

const IconicSlide: FC<IconicCarouselProps> = ({
  headerLinkAs,
  headerLinkHref,
  localeGenderId,
  maxItems,
  products,
  slidesDesktop,
  slidesMobile,
  slidesTablet,
  subTitle,
  title,
  labels,
}) => {
  const { isMobile } = useBreakpoints();

  const [imageElementWrapperHeight, getImageElementWrapper] = useImageHeight();

  const listFormat = SEGMENT_LIST_FORMAT.carousel;
  const listId = title || "";
  const listType = SEGMENT_LIST_TYPE.curatedCategory;
  const { enableGatedHomepage } = useFeatureFlags();
  const isLoggedIn = useIsLoggedIn();

  const { segmentProductOnClick, segmentViewAllOnClick, setVisibleProducts } =
    useSegmentProductList({
      listFormat,
      listId,
      listType,
      numberOfItems: products?.length || 0,
      shopType: localeGenderId,
    });

  const setRedirectData = (
    type: string,
    product?: ProductRedirect,
    actionSource?: string
  ) => {
    removeStorageItem(GATED_POPUP_REDIRECT_DATA);
    setStorageJSON(GATED_POPUP_REDIRECT_DATA, {
      gatedActionType: type,
      numberOfItems: products?.length || 0,
      product,
      productListMeta: {
        listFormat,
        listId,
        listType,
        shopType: localeGenderId,
      },
      actionSource: actionSource || "view_all",
    });
  };

  const noViewAll = enableGatedHomepage && !isLoggedIn;

  return (
    <>
      <CarouselHeader
        title={title}
        subTitle={subTitle}
        href={noViewAll ? undefined : headerLinkHref}
        as={headerLinkAs}
        prefetch={false}
        viewAllOnClick={noViewAll ? undefined : segmentViewAllOnClick}
      />
      <Carousel
        slidesDesktop={slidesDesktop}
        slidesTablet={slidesTablet}
        slidesMobile={slidesMobile}
        marginSize={isMobile ? "m" : "l"}
        maxItems={maxItems}
        imageElementWrapperHeight={imageElementWrapperHeight}
        items={
          products
            ? products?.map(
                (product: DeprecatedAlgoliaSearchProduct, index) => {
                  const productProps = {
                    ...product,
                    id: product.objectID,
                    name: product.name,
                    price: product.price,
                    regular_price: product.regular_price,
                    brand: {
                      name: product.brand_name || "",
                    } as CatalogBrand,
                    thumbnail: product.image || "",
                    labels: labels?.[product.slug] || product.labels,
                    available_sizes_label: "",
                  } as CatalogProduct;

                  if (
                    headerLinkHref &&
                    index === products.length - 2 &&
                    !noViewAll
                  ) {
                    return (
                      <CarouselOverlay
                        key={product.objectID}
                        link={headerLinkHref}
                        onClick={segmentViewAllOnClick}
                      >
                        <ProductCardWishlist
                          {...product}
                          product={productProps}
                          hideIconsLabel
                          position={index}
                          segmentProductOnClick={segmentProductOnClick}
                          setVisibleProducts={setVisibleProducts}
                          getImageElementWrapper={getImageElementWrapper}
                        />
                      </CarouselOverlay>
                    );
                  }
                  return (
                    <ProductCardWishlist
                      key={product.objectID}
                      {...product}
                      product={productProps}
                      hideIconsLabel
                      position={index}
                      segmentProductOnClick={segmentProductOnClick}
                      setVisibleProducts={setVisibleProducts}
                      getImageElementWrapper={getImageElementWrapper}
                      trackingData={{
                        pageName: `${
                          PLP_PAGE_TYPE[PLPType.ICONS_PLP]
                        }_product_listing_page`,
                        shopType: localeGenderId,
                        carouselType: "icons",
                      }}
                      setProductRedirect={setRedirectData}
                    />
                  );
                }
              )
            : []
        }
      />
    </>
  );
};

export default IconicSlide;
